import { useQuery } from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";

import type {
  GetCosignQueueResponse,
  GetCosignQueueSearchParams,
} from "@/core/types/(api)/cosignature";
import { SHORT_LIVED_CACHE } from "@/lib/constants/cache";

import { cosignatureKeys } from "../keyFactory";
import { getCosignatureQueue } from "../queryFunctions/getCosignatureQueue";

type UseCosignatureQueueProps<TData = GetCosignQueueResponse> = {
  enabled?: boolean;
  searchParams?: GetCosignQueueSearchParams;
  select?: (data: GetCosignQueueResponse) => TData;
};

type CosignatureQueueQueryOptions = UseQueryOptions<
  GetCosignQueueResponse,
  unknown,
  GetCosignQueueResponse,
  ReturnType<typeof cosignatureKeys.queue>
>;

export function optionFactory(searchParams?: GetCosignQueueSearchParams, enabled = true) {
  return {
    enabled,
    queryFn: ({ signal }) => getCosignatureQueue(searchParams, signal),
    queryKey: cosignatureKeys.queue(searchParams),
    refetchOnWindowFocus: true,
    staleTime: SHORT_LIVED_CACHE,
  } satisfies CosignatureQueueQueryOptions;
}

export function useCosignatureQueue<TData = GetCosignQueueResponse>({
  enabled,
  searchParams,
  select,
}: UseCosignatureQueueProps<TData> = {}) {
  return useQuery({ ...optionFactory(searchParams, enabled), keepPreviousData: true, select });
}
